import { useRouter } from "next/router";
import { useEffect } from "react";
import { useQuery } from "react-query";

import { PACKAGE_KEY } from "goi_common";

import { getAdvancePayPackages } from "@/apis/payment";
import queryKeys from "@/constants/querykeys";
import { sessionStorage, storageKeys, tossLocalStorage } from "@/utils/storage";
import { PRODUCT_GROUP } from "@/constants/productKey";
import {
  funnelDetailValues,
  landingFunnelKey,
} from "@/constants/funnelRouteDeclareValues";

/**
 * 별도 랜딩 페이지를 거치지 않고 productKey 단독으로 verification에 직진하는 상품들.
 * 이전 funnel(generalFunnels) 잔재가 남으면 결제 옵션/구좌 흐름이 오염되므로 진입 시 정리한다.
 * 사용처:
 * - PACKAGE_KEY.SALES[289].PAY1DC20: (레거시) 잔존 링크 대비 유지
 * - PACKAGE_KEY.PAYBK[289].V12: /my/v2 PrepaidDiscount, /message/last-check PerkSection
 * - PACKAGE_KEY.SALES[289].PAY10DC10: /product/consult/exclusive
 * - PACKAGE_KEY.SALES[289].PAY1DC10: /promotion/service/202601-1
 * - PACKAGE_KEY.SALES[289].PAY1DC20BNF: /promotion/service/beneficiary-2026
 */
const PRODUCT_KEYS_REQUIRING_FUNNEL_RESET = new Set<string>([
  PACKAGE_KEY.SALES[289].PAY1DC20,
  PACKAGE_KEY.PAYBK[289].V12,
  PACKAGE_KEY.SALES[289].PAY10DC10,
  PACKAGE_KEY.SALES[289].PAY1DC10,
  PACKAGE_KEY.SALES[289].PAY1DC20BNF,
]);

/**
 * HBF productGroup에 대해 couponCode 유무로 funnelKey 반환
 */
const getHbfFunnelKey = ({
  productGroup,
  couponCode,
  fnk,
}: {
  productGroup: string;
  couponCode?: string | null;
  fnk?: string;
}): string | null => {
  if (productGroup === PRODUCT_GROUP["hbf"]) {
    // 외부에서 특정 fnk(예: hbf_mixed_v2)를 명시적으로 넘긴 경우 레거시 분기를 타지 않고 그대로 반환
    if (fnk === landingFunnelKey.GROUP["hbf_mixed_v2"]) {
      return fnk;
    }
    if (fnk === landingFunnelKey.GROUP["hbf_mixed_mt"]) {
      return fnk;
    }
    // couponCode 있으면 혼합 (100 + 9900), 없으면 9900 전용
    return couponCode
      ? landingFunnelKey.GROUP["hbf_mixed"]
      : landingFunnelKey.GROUP["hbf_9900"];
  }
  return null;
};

/**
 * 별도 랜딩 페이지를 거치지 않고 verification으로 직진하는 케이스용.
 * fnk가 명시되어 있고 funnelDetailValues에 매핑이 있으면 그대로 반환한다.
 */
const getDirectFunnelKey = ({ fnk }: { fnk?: string }): string | null => {
  if (fnk && funnelDetailValues[fnk]) return fnk;
  return null;
};

export const useSetupRegistrationContext = (options?: {
  skipRedirect?: boolean;
}) => {
  const router = useRouter();
  const { productKey, productGroup, account, couponCode, registered_ref, fnk } =
    router.query as {
      productKey?: string;
      productGroup?: string;
      account?: string;
      couponCode?: string;
      registered_ref?: string;
      fnk?: string;
    };

  const { data, status } = useQuery(
    queryKeys.advancePayCustomers(productKey || ""),
    () => getAdvancePayPackages(productKey || ""),
    {
      retry: 0,
      enabled: !!productKey,
    }
  );

  const isToss = router.pathname.includes("toss");
  const redirectPath = isToss
    ? "/registration/toss/failure/"
    : "/registration/failure/";

  useEffect(() => {
    if (!router.isReady) return;

    if (account) {
      sessionStorage.set(storageKeys.Account, String(account));
      tossLocalStorage.set(storageKeys.Account, String(account));
    }
    if (couponCode) {
      sessionStorage.set(storageKeys.CouponCode, String(couponCode));
      tossLocalStorage.set(storageKeys.CouponCode, String(couponCode));
    }

    if (productGroup) {
      sessionStorage.set(storageKeys.productGroup, String(productGroup));
      tossLocalStorage.set(storageKeys.productGroup, String(productGroup));

      // 쿼리에 couponCode가 없으면 이전 세션의 couponCode 제거
      // (쿼리 파라미터가 source of truth)
      if (!couponCode) {
        sessionStorage.remove(storageKeys.CouponCode);
        tossLocalStorage.remove(storageKeys.CouponCode);
      }

      // productGroup → funnelKey 자동 매핑
      // 1) HBF: couponCode 유무로 구분 (레거시)
      // 2) 범용: fnk 쿼리 파라미터가 funnelDetailValues에 존재하면 그대로 사용
      // 쿼리 파라미터가 source of truth (sessionStorage는 이탈 후 남아있을 수 있음)
      const funnelKey =
        getHbfFunnelKey({
          productGroup: String(productGroup),
          couponCode: couponCode || null,
          fnk,
        }) ?? getDirectFunnelKey({ fnk });
      if (funnelKey) {
        const funnelDetails = funnelDetailValues[funnelKey];

        if (funnelDetails) {
          sessionStorage.set(
            storageKeys.generalFunnels,
            JSON.stringify({
              key: funnelKey,
              paths: funnelDetails.paths,
              product: funnelDetails.product,
            })
          );
        }
      }

      return;
    }

    if (registered_ref) {
      sessionStorage.set(storageKeys.registeredRef, String(registered_ref));
      tossLocalStorage.set(storageKeys.registeredRef, String(registered_ref));
    }

    if (productKey) {
      // productKey 단독으로 직진 진입하는 상품들은 이전 funnel/productGroup 잔재를 정리.
      // 특히 productGroup이 남아 있으면 useConfirmationPath가 옵션 시트 페이지(confirmation-contract)로
      // 라우팅해서 productKey가 덮어쓰기되는 문제가 있음. 잔재 정리해야 one-key/confirmation-contract로
      // 라우팅되어 옵션 시트 없이 바로 결제 진행됨.
      if (PRODUCT_KEYS_REQUIRING_FUNNEL_RESET.has(String(productKey))) {
        sessionStorage.remove(storageKeys.generalFunnels);
        sessionStorage.remove(storageKeys.productGroup);
      }

      sessionStorage.set(storageKeys.ProductKey, String(productKey));
      tossLocalStorage.set(storageKeys.ProductKey, String(productKey));
    } else if (!options?.skipRedirect) {
      router.replace(redirectPath);
    }
  }, [
    account,
    productKey,
    productGroup,
    couponCode,
    router,
    registered_ref,
    options?.skipRedirect,
  ]);

  useEffect(() => {
    if (productGroup) {
      return;
    }

    if (status === "idle" || status === "loading") {
      return;
    }

    if (status === "error") {
      router.replace(redirectPath);
      return;
    }

    if (data?.data.is_on_sale === false) {
      router.replace(redirectPath);
      return;
    }
  }, [status, data]);

  return {
    data,
    status,
  };
};
